import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { AuthProviderEnum } from '@sport-activities/sdk';
import { from, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { AuthService } from '~/auth/services/auth/auth.service';
import { UserService } from '~/auth/services/user/user.service';
import { CheckoutService } from '~/checkout/services/checkout/checkout.service';

@Injectable({
  providedIn: 'root',
})
class PermissionsService {
  constructor(
    private readonly checkoutService: CheckoutService,
    private readonly userService: UserService,
    private readonly authService: AuthService,
    private readonly router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | UrlTree {
    const bookingItemId = route.queryParams['bookingItemId'];

    // no item in checkout = no checkout, unless we have bookingItemId from email
    if (!this.checkoutService.hasItems() && !bookingItemId) {
      return this.router.navigate(['/']);
    }

    return this.userService.isAuthenticated().pipe(
      mergeMap((isLogged) => {
        // user can access checkout if not logged
        if (!isLogged) {
          return of(true);
        }

        const user = this.userService.getUser();

        // logout fed users
        if (user.providerName === ('FED' as AuthProviderEnum)) {
          return from(this.authService.logout()).pipe(map(() => false));
        }

        return of(true);
      })
    );
  }
}

export const CanAccessCheckoutGuard: CanActivateFn = (
  route,
  state
): Observable<boolean> | Promise<boolean> | UrlTree => {
  return inject(PermissionsService).canActivate(route, state);
};
